import { useState } from 'react';
import { Link } from 'react-router-dom';
import { aosphereURL, aosphereYouTubeURL, publicURL } from '../util/constants';
import { captureEventInternal } from '../util/posthog';
import ConditionOfUseModal from './Modals/ConditionOfUseModel';
import CookiesPolicyModal from './Modals/CookiesPolicyModal';
import LegalNoticesModal from './Modals/LegalNoticesModal';
import ModernSlaveryModal from './Modals/ModernSlaveryModal';
import { LinkedInIcon, YouTubeIcon } from './BrandIcons';
import '../error/errorPage.css';

const handleFooterLinkClick = (text: string, href: string) => {
  void captureEventInternal('footer_links', { text, href });
};

interface FooterProps {
  /** 'dark' renders the white-on-transparent, stacked (icons / links / address+copyright)
   * layout used on the error pages' gradient background — see the 404 mockup.
   * @default 'light' */
  variant?: 'light' | 'dark';
}

const Footer = ({ variant = 'light' }: FooterProps) => {
  const [showCookiesPolicyModal, setShowCookiesPolicyModal] = useState(false);
  const [showLegalNoticesModal, setShowLegalNoticesModal] = useState(false);
  const [showModernSlaveryModal, setShowModernSlaveryModal] = useState(false);
  const currentYear = new Date().getFullYear();

  if (variant === 'dark') {
    return (
      <footer className="ao-errorCard-body d-flex flex-column" style={{ color: '#fff', padding: '26px 40px 22px' }}>
        <div className="ao_mainContainer d-flex flex-column gap-3">
          <div className="d-flex gap-3">
            <a
              href="https://www.linkedin.com/company/aosphere"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="ao-footer-social-link d-inline-flex align-items-center justify-content-center"
            >
              <LinkedInIcon width={40} height={40} />
            </a>
            <a
              href={aosphereYouTubeURL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="ao-footer-social-link d-inline-flex align-items-center justify-content-center"
            >
              <YouTubeIcon width={40} height={40} />
            </a>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3" style={{ fontSize: 14, fontWeight: 600 }}>
            <ConditionOfUseModal linkStyle={{ color: '#fff', textDecoration: 'underline' }} />
            <span style={{ opacity: 0.5 }}>|</span>
            <Link to={`${aosphereURL}/privacy-statement/`} target="_blank" className="ao-footer-dark-link" onClick={() => handleFooterLinkClick('Privacy Statement', `${aosphereURL}/privacy-statement/`)}>
              Privacy statement
            </Link>
            <span style={{ opacity: 0.5 }}>|</span>
            <Link
              to="#"
              className="ao-footer-dark-link"
              onClick={(event) => {
                event.preventDefault();
                setShowCookiesPolicyModal(true);
                handleFooterLinkClick('Cookies Policy', `${publicURL}/docs/CookiePolicy.pdf`);
              }}
            >
              Cookies policy
            </Link>
            <span style={{ opacity: 0.5 }}>|</span>
            <Link
              to="#"
              className="ao-footer-dark-link"
              onClick={(event) => {
                event.preventDefault();
                setShowLegalNoticesModal(true);
                handleFooterLinkClick('Legal Notices', `${publicURL}/docs/LegalNotices.pdf`);
              }}
            >
              Legal notices
            </Link>
            <span style={{ opacity: 0.5 }}>|</span>
            <Link
              to="#"
              className="ao-footer-dark-link"
              onClick={(event) => {
                event.preventDefault();
                setShowModernSlaveryModal(true);
                handleFooterLinkClick('Modern Slavery', `${publicURL}/docs/ModernSlavery.pdf`);
              }}
            >
              Modern Slavery
            </Link>
          </div>

          <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-3" style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            <span>
              aosphere Limited registered in England and Wales with registered number 15371365 and registered office at
              The Steward Building, 12 Steward Street, London, E1 6FQ, United Kingdom.
            </span>
            <span style={{ fontSize: 16, fontWeight: 500, color: '#fff' }}>© aosphere {currentYear}</span>
          </div>
        </div>

        <CookiesPolicyModal show={showCookiesPolicyModal} onHide={() => setShowCookiesPolicyModal(false)} />
        <LegalNoticesModal show={showLegalNoticesModal} onHide={() => setShowLegalNoticesModal(false)} />
        <ModernSlaveryModal show={showModernSlaveryModal} onHide={() => setShowModernSlaveryModal(false)} />
      </footer>
    );
  }

  return (
    <footer className="ao_footer">
      <div className="row ao_mainContainer">
        <div className="d-flex align-items-center ao_footerLinks">
          <div className="col">
            © aosphere {currentYear} <span className="pipeline">|</span>
            <ConditionOfUseModal />
            <span className="pipeline">|</span>
            <Link
              to={`${aosphereURL}/privacy-statement/`}
              target="_blank"
              onClick={() =>
                handleFooterLinkClick('Privacy Statement', `${aosphereURL}/privacy-statement/`)
              }
            >
              Privacy Statement
            </Link>{' '}
            <span className="pipeline">|</span>
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                setShowCookiesPolicyModal(true);
                handleFooterLinkClick('Cookies Policy', `${publicURL}/docs/CookiePolicy.pdf`)
              }}
            >
              Cookies Policy
            </Link>{' '}
            <span className="pipeline">|</span>
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                setShowLegalNoticesModal(true);
                handleFooterLinkClick('Legal Notices', `${publicURL}/docs/LegalNotices.pdf`)
              }}
            >
              Legal Notices
            </Link>{' '}
            <span className="pipeline">|</span>
            <Link
              to="#"
              onClick={(event) => {
                event.preventDefault();
                setShowModernSlaveryModal(true);
                handleFooterLinkClick('Modern Slavery', `${publicURL}/docs/ModernSlavery.pdf`);
              }}
            >
              Modern Slavery
            </Link>
            <br />
            <p className="greyFont semiboldNone form-text mb-0">
              aosphere Limited registered in England and Wales with registered
              number 15371365 and registered office at The Steward Building, 12
              Steward Street, London, E1 6FQ, United Kingdom.
            </p>
          </div>
          <div className="ms-auto">
            <Link
              to="https://www.linkedin.com/company/aosphere"
              target="_blank"
            >
              <svg
                width="32"
                height="32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.636 13.091a1.455 1.455 0 100-2.909 1.455 1.455 0 000 2.91zM13.09 13.818h-2.908v8h2.909v-8z"
                  fill="#6E6F81"
                ></path>
                <path
                  d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0zm0 30.545C7.967 30.545 1.455 24.033 1.455 16 1.455 7.967 7.967 1.455 16 1.455c8.033 0 14.545 6.512 14.545 14.545 0 8.033-6.512 14.545-14.545 14.545z"
                  fill="#6E6F81"
                ></path>
                <path
                  d="M19.898 13.818c-2.182 0-2.443 1.295-2.443 1.295v-1.295h-2.91v8h2.91v-4.364S17.455 16 18.69 16c.727 0 .945.647.945 1.454v4.364h2.91v-4.364c0-2.276-.99-3.636-2.648-3.636z"
                  fill="#6E6F81"
                ></path>
              </svg>
            </Link>
          </div>
        </div>
      </div>
      <CookiesPolicyModal
        show={showCookiesPolicyModal}
        onHide={() => setShowCookiesPolicyModal(false)}
      />
      <LegalNoticesModal
        show={showLegalNoticesModal}
        onHide={() => setShowLegalNoticesModal(false)}
      />
      <ModernSlaveryModal
        show={showModernSlaveryModal}
        onHide={() => setShowModernSlaveryModal(false)}
      />
    </footer>
  );
};

export default Footer;
